import { devDebug } from "../lib/devLog";
import { useCallback, useEffect, useRef, useState } from "react";

// Wrapper around MediaRecorder + getUserMedia + Web Audio AnalyserNode.
// Exposes a clean, minimal API for the ComposerBar. Provides a real-time
// audio level (0..1) sampled from the AnalyserNode so the waveform can
// react to the user's actual voice instead of a pre-rendered pattern.
//
// Voice presets:
//   • "off"     — raw recording, no processing
//   • "anonimo" — real-time timbre disguise (biquad filters + soft-clip +
//                 LFO-modulated delay). No pitch shift. Same duration.
//   • "grave"   — post-process pitch shift down (~-4 semitones). WAV output.
//                 Duration lengthens ~25% as a natural side-effect.
//   • "acuto"   — post-process pitch shift up   (~+4 semitones). WAV output.
//                 Duration shortens ~20%.

const MAX_SECONDS = 180;
const LEVEL_HISTORY_SIZE = 46;

// Semitone offsets for the pitch-shift presets. Chosen to disguise the
// speaker without turning the voice into an unintelligible chipmunk.
const PRESET_SEMITONES = {
  grave: -4,
  acuto: 4,

  brillante: 2,
  caldo: -2,
  acuto_intenso: 6,
  profondo: -6,

  creator_high: 8,
  creator_deep: -8,
};

function isNativeApp() {
  return Boolean(window.Capacitor?.isNativePlatform?.());
}

async function getMicrophoneStream() {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {
    const permissionLike = error?.name === "NotAllowedError" || error?.name === "SecurityError";
    if (!isNativeApp() || !permissionLike) throw error;
    // Capacitor and Android complete their permission callbacks on separate
    // event-loop turns. Retry once before treating a transient denial as a
    // revoked permission.
    await new Promise((resolve) => window.setTimeout(resolve, 350));
    return navigator.mediaDevices.getUserMedia({ audio: true });
  }
}

function computeRms(sample) {
  let sum = 0;
  for (let i = 0; i < sample.length; i++) {
    const v = (sample[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / sample.length);
}

function makeSoftClipCurve(amount = 0.35) {
  const samples = 1024;
  const curve = new Float32Array(samples);
  const k = amount * 40;
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + k) * x) / (3 + k * Math.abs(x));
  }
  return curve;
}

function buildAnonymousGraph(ctx, sourceStream) {
  const src = ctx.createMediaStreamSource(sourceStream);

  const lowShelf = ctx.createBiquadFilter();
  lowShelf.type = "lowshelf";
  lowShelf.frequency.value = 320;
  lowShelf.gain.value = 8;

  const highShelf = ctx.createBiquadFilter();
  highShelf.type = "highshelf";
  highShelf.frequency.value = 2800;
  highShelf.gain.value = -10;

  const peaking = ctx.createBiquadFilter();
  peaking.type = "peaking";
  peaking.frequency.value = 1150;
  peaking.Q.value = 0.9;
  peaking.gain.value = -9;

  const shaper = ctx.createWaveShaper();
  shaper.curve = makeSoftClipCurve(0.5);
  shaper.oversample = "2x";

  const delay = ctx.createDelay(0.05);
  delay.delayTime.value = 0.012;
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 4.5;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.006;
  lfo.connect(lfoGain).connect(delay.delayTime);
  lfo.start();

  const outGain = ctx.createGain();
  outGain.gain.value = 1.1;

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;

  const dest = ctx.createMediaStreamDestination();

  src.connect(lowShelf);
  lowShelf.connect(highShelf);
  highShelf.connect(peaking);
  peaking.connect(shaper);
  shaper.connect(delay);
  delay.connect(outGain);
  outGain.connect(analyser);
  outGain.connect(dest);

  return { stream: dest.stream, analyser, nodes: [lfo] };
}

// --- WAV encoder + offline pitch shifter --------------------------------

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numSamples = buffer.length;
  const dataLength = numSamples * numCh * 2;
  const ab = new ArrayBuffer(44 + dataLength);
  const view = new DataView(ab);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numCh * 2, true);
  view.setUint16(32, numCh * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataLength, true);
  let offset = 44;
  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < numSamples; i++) {
    for (let c = 0; c < numCh; c++) {
      let s = Math.max(-1, Math.min(1, channels[c][i]));
      s = s < 0 ? s * 0x8000 : s * 0x7fff;
      view.setInt16(offset, s, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

// Offline pitch shift via playbackRate. Duration changes proportionally —
// acceptable trade-off for an MVP voice modulator without a phase vocoder.
async function pitchShiftBlob(blob, semitones) {
  const arrayBuffer = await blob.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  const decodeCtx = new AC();
  const decoded = await decodeCtx.decodeAudioData(arrayBuffer);
  await decodeCtx.close();

  const pitchFactor = Math.pow(2, semitones / 12);
  const outputLen = Math.max(1, Math.round(decoded.length / pitchFactor));
  const offline = new OfflineAudioContext(
    decoded.numberOfChannels,
    outputLen,
    decoded.sampleRate,
  );
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.playbackRate.value = pitchFactor;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  return audioBufferToWavBlob(rendered);
}

// ------------------------------------------------------------------------

export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState(null);
  const [permissionIssue, setPermissionIssue] = useState(null);
  const [levels, setLevels] = useState([]);
  const [preset, setPreset] = useState("off"); // off | anonimo | grave | acuto
  const [processing, setProcessing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const tickTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const extraNodesRef = useRef([]);
  const alteredStreamRef = useRef(null);
  const activePresetRef = useRef("off");

  const teardownAudio = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    extraNodesRef.current.forEach((n) => {
      try { n.stop && n.stop(); } catch (err) { devDebug("audio node.stop failed", err?.message); }
      try { n.disconnect && n.disconnect(); } catch (err) { devDebug("audio node.disconnect failed", err?.message); }
    });
    extraNodesRef.current = [];
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    if (alteredStreamRef.current) {
      alteredStreamRef.current.getTracks().forEach((t) => t.stop());
      alteredStreamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      teardownAudio();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl, teardownAudio]);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    teardownAudio();
    setRecording(false);
  }, [teardownAudio]);

  const start = useCallback(async () => {
    setError(null);
    setPermissionIssue(null);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setDurationMs(0);
    setSeconds(0);
    setLevels([]);
    setProcessing(false);
    activePresetRef.current = preset;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Il browser non supporta la registrazione audio");
      return;
    }
    try {
      const stream = await getMicrophoneStream();
      streamRef.current = stream;

      let recordStream = stream;
      let analyser = null;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        audioCtxRef.current = ctx;
        if (preset === "anonimo") {
          const graph = buildAnonymousGraph(ctx, stream);
          recordStream = graph.stream;
          alteredStreamRef.current = graph.stream;
          analyser = graph.analyser;
          extraNodesRef.current = graph.nodes;
        } else {
          const src = ctx.createMediaStreamSource(stream);
          analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          src.connect(analyser);
        }
        analyserRef.current = analyser;
      }

      const mr = new MediaRecorder(recordStream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const rawBlob = new Blob(chunksRef.current, {
          type: mr.mimeType || "audio/webm",
        });
        const capturedMs = Date.now() - startTsRef.current;
        const usedPreset = activePresetRef.current;
        const shiftBy = PRESET_SEMITONES[usedPreset];
        if (shiftBy) {
          setProcessing(true);
          try {
            const shifted = await pitchShiftBlob(rawBlob, shiftBy);
            const url = URL.createObjectURL(shifted);
            setBlobUrl(url);
            // Post-processing changes duration by 1/pitchFactor.
            const pitchFactor = Math.pow(2, shiftBy / 12);
            setDurationMs(Math.round(capturedMs / pitchFactor));
          } catch (err) {
            setError(`Elaborazione voce fallita: ${err?.message || err}`);
            const url = URL.createObjectURL(rawBlob);
            setBlobUrl(url);
            setDurationMs(capturedMs);
          } finally {
            setProcessing(false);
          }
        } else {
          const url = URL.createObjectURL(rawBlob);
          setBlobUrl(url);
          setDurationMs(capturedMs);
        }
      };
      mediaRecorderRef.current = mr;
      startTsRef.current = Date.now();
      mr.start();

      if (analyser) {
        const buffer = new Uint8Array(analyser.fftSize);
        const sampleLoop = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteTimeDomainData(buffer);
          const rms = Math.min(1, computeRms(buffer) * 4);
          setLevels((prev) => {
            const next = prev.length >= LEVEL_HISTORY_SIZE ? prev.slice(1) : prev.slice();
            next.push(rms);
            return next;
          });
          rafRef.current = requestAnimationFrame(sampleLoop);
        };
        rafRef.current = requestAnimationFrame(sampleLoop);
      }

      setRecording(true);
      tickTimerRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= MAX_SECONDS) stop();
          return next;
        });
      }, 1000);
    } catch (err) {
      const denied = err?.name === "NotAllowedError" || err?.name === "SecurityError";
      if (denied) {
        let state = "denied";
        try {
          const status = await navigator.permissions?.query?.({ name: "microphone" });
          state = status?.state || state;
        } catch {}
        setPermissionIssue({ permission: "microphone", state });
        setError("Il microfono non è autorizzato. Tocca qui per riattivarlo.");
      } else {
        setError(`Errore microfono: ${err?.message || err}`);
      }
    }
  }, [preset, blobUrl, stop]);

  const reset = useCallback(() => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setDurationMs(0);
    setSeconds(0);
    setError(null);
    setLevels([]);
  }, [blobUrl]);

  return {
    recording,
    seconds,
    blobUrl,
    durationMs,
    error,
    permissionIssue,
    clearPermissionIssue: () => setPermissionIssue(null),
    levels,
    preset,
    setPreset,
    processing,
    start,
    stop,
    reset,
    maxSeconds: MAX_SECONDS,
  };
}
